import { stats } from './content'
import type { FaqItem, Stat } from './content'

export const kitHero = {
  subheadline: '100 mapas visuales + 6 kits profesionales listos para usar',
  description:
    '100 explicaciones preparadas para tus clientes, 24 sesiones para adaptar y 50 errores con correcciones prácticas. Todo organizado para trabajar con menos improvisación.',
}

export const kitStats: Stat[] = stats.map((stat) =>
  stat.label === 'Bonos prácticos' ? { ...stat, label: 'Kits profesionales' } : stat,
)

export interface Kit {
  number: string
  title: string
  hook: string
  description: string
}

export const kitsCopy = {
  title: 'Y eso no es todo: también recibes 6 kits profesionales listos para usar.',
  subtitle:
    'No solo mapas para consultar. Seis herramientas prácticas para explicar ejercicios, preparar sesiones y organizar el trabajo con tus clientes.',
  badge: 'Incluido',
  closing:
    'Desde explicar un ejercicio hasta preparar la primera sesión: seis herramientas prácticas para trabajar con más orden y menos improvisación.',
  closingNote: 'Los seis kits están incluidos en el pack completo.',
}

export const kits: Kit[] = [
  {
    number: '01',
    title: '100 explicaciones listas para tus clientes',
    hook: 'Las palabras adecuadas para explicar cada ejercicio.',
    description:
      '100 explicaciones ya estructuradas, con músculos implicados, técnica e indicaciones prácticas. Abre la ficha y adáptala a tu cliente.',
  },
  {
    number: '02',
    title: 'Matriz de alternativas de ejercicios',
    hook: 'Encuentra una alternativa adecuada sin empezar de cero.',
    description:
      'Una matriz de consulta con variantes organizadas por movimiento, nivel y material disponible.',
  },
  {
    number: '03',
    title: '24 sesiones listas para adaptar',
    hook: 'Deja de empezar con una hoja en blanco.',
    description:
      '24 fichas con ejercicios, series, repeticiones y secuencias ya organizadas. Elige la sesión y personalízala para tu cliente.',
  },
  {
    number: '04',
    title: 'Correcciones exprés: 50 errores comunes',
    hook: 'Identifica el error. Consulta qué observar y cómo corregirlo.',
    description:
      '50 errores técnicos frecuentes con indicaciones prácticas para explicar las correcciones con mayor claridad.',
  },
  {
    number: '05',
    title: 'Kit de fichas y seguimiento de clientes',
    hook: 'Menos hojas sueltas. Más control sobre los progresos.',
    description:
      'Plantillas listas para registrar los entrenamientos, las cargas, las repeticiones y la evolución de tus clientes.',
  },
  {
    number: '06',
    title: 'Kit de primera sesión sin improvisar',
    hook: 'La primera sesión, ya organizada.',
    description:
      'Listas de comprobación, fichas de objetivos y una estructura para organizar el primer encuentro con tus nuevos clientes.',
  },
]

export interface KitOfferRow {
  title: string
  detail: string
  status: string
}

export const kitOffer = {
  label: 'Pack completo para entrenadores personales',
  name: 'Atlas Kinesio-Visual',
  summary: '100 mapas visuales + 6 kits profesionales',
  bundle: '1 Atlas completo + 6 kits profesionales',
  bundleNote: 'Todo organizado. Una única compra. Sin suscripciones.',
  priceLabel: 'Todo el sistema por solo',
  price: '19',
  currency: '€',
  priceNote: 'Pago único. Acceso de por vida.',
  cta: 'Sí, quiero el sistema completo',
  note: 'Pago seguro · Descarga inmediata · Garantía de 7 días',
  closing:
    '100 ejercicios analizados y 6 herramientas listas para utilizar en tu trabajo. Todo en un único pack digital.',
}

export const kitOfferRows: KitOfferRow[] = [
  {
    title: 'Atlas visual — 100 mapas de ejercicios',
    detail: 'Músculos, técnica, planos anatómicos, errores comunes y variantes.',
    status: 'Incluido',
  },
  {
    title: 'Kits de explicaciones y alternativas',
    detail: '100 explicaciones preparadas + matriz de alternativas de ejercicios.',
    status: 'Incluidos',
  },
  {
    title: 'Kits de sesiones y correcciones',
    detail: '24 sesiones para adaptar + 50 errores técnicos con indicaciones prácticas.',
    status: 'Incluidos',
  },
  {
    title: 'Kits de clientes y primera sesión',
    detail: 'Fichas de seguimiento + herramientas para organizar el primer encuentro.',
    status: 'Incluidos',
  },
  {
    title: 'Acceso digital completo',
    detail: 'PDF imprimibles · Acceso inmediato · Acceso de por vida.',
    status: 'Incluido',
  },
]

export const kitFinalCta = {
  title: '100 ejercicios analizados. 6 kits listos para usar. Un único sistema.',
  subtitle:
    'Reúne en un solo pack las referencias y herramientas para explicar ejercicios, preparar sesiones y organizar el trabajo con tus clientes.',
  summary: '100 mapas visuales + 6 kits profesionales',
  cta: 'Quiero el sistema completo',
}

export const kitFaqs: FaqItem[] = [
  {
    question: '¿Es adecuado para entrenadores personales que están empezando?',
    answer:
      'Sí. Los mapas ayudan a conectar la anatomía con ejercicios reales, y los kits aportan explicaciones, sesiones adaptables y plantillas para organizar tu trabajo.',
  },
  {
    question: '¿Puedo consultarlo desde el móvil mientras trabajo?',
    answer:
      'Sí. Puedes abrir los PDF desde el móvil, la tablet o el ordenador cuando necesites revisar un ejercicio o consultar una de las herramientas.',
  },
  {
    question: '¿Incluye sesiones de entrenamiento ya preparadas?',
    answer:
      'Sí. Uno de los kits incluye 24 sesiones con ejercicios, series, repeticiones y secuencias organizadas. Son una base de trabajo que debes adaptar al nivel, los objetivos y las necesidades de cada cliente.',
  },
  {
    question: '¿Puedo imprimir los mapas?',
    answer:
      'Sí. Los mapas y los seis kits se entregan en PDF imprimibles. Puedes consultar el material en formato digital o imprimir las páginas que necesites.',
  },
  {
    question: '¿Cómo recibo el Atlas? ¿Tengo que pagar todos los meses?',
    answer:
      'Recibes acceso al material digital después de completar la compra. Es un único pago de 19 €, con los seis kits incluidos y sin suscripciones.',
  },
  {
    question: '¿Qué ocurre si no estoy satisfecho?',
    answer:
      'Puedes solicitar la devolución durante los 7 días posteriores a la compra, según las condiciones de la garantía.',
  },
]
